import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface ItemDraft {
  tempId: string;
  label: string;
  description?: string | null;
  required: boolean;
}

export function newItemDraft(): ItemDraft {
  return { tempId: crypto.randomUUID(), label: '', required: false };
}

interface ChecklistItemComposerProps {
  value: ItemDraft[];
  onChange: (next: ItemDraft[]) => void;
}

export default function ChecklistItemComposer({ value, onChange }: ChecklistItemComposerProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const [focusTempId, setFocusTempId] = useState<string | null>(null);

  const update = useCallback(
    (tempId: string, patch: Partial<Omit<ItemDraft, 'tempId'>>) => {
      onChange(value.map((it) => (it.tempId === tempId ? { ...it, ...patch } : it)));
    },
    [value, onChange],
  );

  const remove = useCallback(
    (tempId: string) => {
      const next = value.filter((it) => it.tempId !== tempId);
      // Always keep at least one row.
      onChange(next.length === 0 ? [newItemDraft()] : next);
    },
    [value, onChange],
  );

  const append = useCallback(() => {
    const fresh = newItemDraft();
    onChange([...value, fresh]);
    setFocusTempId(fresh.tempId);
  }, [value, onChange]);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const oldIndex = value.findIndex((it) => it.tempId === active.id);
      const newIndex = value.findIndex((it) => it.tempId === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onChange(arrayMove(value, oldIndex, newIndex));
    },
    [value, onChange],
  );

  return (
    <div className="space-y-3" data-testid="item-composer">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={value.map((it) => it.tempId)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-2">
            {value.map((it, i) => (
              <SortableItemRow
                key={it.tempId}
                item={it}
                index={i}
                isLast={i === value.length - 1}
                autoFocus={focusTempId === it.tempId}
                onAutoFocusHandled={() => setFocusTempId(null)}
                onUpdate={(patch) => update(it.tempId, patch)}
                onRemove={() => remove(it.tempId)}
                onEnterFromLast={append}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <Button
        type="button"
        variant="outline"
        onClick={append}
        className="h-12 w-full gap-2 font-mono text-[11px] uppercase tracking-wider"
        data-testid="item-add"
      >
        <Plus className="h-4 w-4" aria-hidden /> Add item
      </Button>
    </div>
  );
}

interface SortableItemRowProps {
  item: ItemDraft;
  index: number;
  isLast: boolean;
  autoFocus: boolean;
  onAutoFocusHandled: () => void;
  onUpdate: (patch: Partial<Omit<ItemDraft, 'tempId'>>) => void;
  onRemove: () => void;
  onEnterFromLast: () => void;
}

function SortableItemRow({
  item,
  index,
  isLast,
  autoFocus,
  onAutoFocusHandled,
  onUpdate,
  onRemove,
  onEnterFromLast,
}: SortableItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.tempId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      onAutoFocusHandled();
    }
  }, [autoFocus, onAutoFocusHandled]);

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="item-row"
      className={cn(
        'flex items-stretch gap-2 rounded-md border bg-background',
        isDragging && 'z-10 shadow-lg ring-1 ring-primary/30',
      )}
    >
      <button
        type="button"
        className="flex h-12 w-10 shrink-0 cursor-grab touch-none items-center justify-center rounded-l-md text-muted-foreground hover:bg-accent active:cursor-grabbing"
        aria-label={`Reorder item ${index + 1}`}
        data-testid="item-handle"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>

      <Input
        ref={inputRef}
        value={item.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (isLast) onEnterFromLast();
            else (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={`Item ${index + 1}`}
        className="h-12 flex-1 border-0 bg-transparent px-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        data-testid="item-label"
      />

      <button
        type="button"
        onClick={() => onUpdate({ required: !item.required })}
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center border-l text-[10px] uppercase tracking-wider',
          item.required
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent',
        )}
        aria-pressed={item.required}
        aria-label={item.required ? 'Required (toggle off)' : 'Mark required'}
        title={item.required ? 'Required' : 'Optional'}
        data-testid="item-required"
      >
        {item.required ? <Check className="h-4 w-4" aria-hidden /> : <span>opt</span>}
      </button>

      <button
        type="button"
        onClick={onRemove}
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-r-md border-l text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        aria-label={`Delete item ${index + 1}`}
        data-testid="item-delete"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}
